import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { environment } from '../../environments/environment';
import { ClaimGuard } from '../route-guards/claim.route-guard';
import { HomePageRouteGuard } from '../route-guards/home-page.route-guard';
import { UnsavedChangesGuard } from '../route-guards/unsaved-changes.route-guard';
import { FormFieldConstraintsComponent } from '../shared/components/form-field-constraints/form-field-constraints.component';
import { NotificationCenterComponent } from '../shared/components/notification-center/notification-center.component';
import { MagicStructureSetupDialog } from '../shared/dialogs/magic-structure-setup/magic-structure-setup.dialog';
import { DocumentPublishingSettingsResolver } from '../shared/resolvers/document-publishing-settings.resolver';
import { FolderResolver } from '../shared/resolvers/folder.resolver';
import { FormInstanceResolver } from '../shared/resolvers/form-instance.resolver';
import { FormResolver } from '../shared/resolvers/form.resolver';
import { NotificationCenterResolver } from "../shared/resolvers/notification-center.resolver";
import { PrintableFormInstanceResolver } from '../shared/resolvers/printable-form-instance.resolver';
import { SiteHomePageResolver } from '../shared/resolvers/site-home-page.resolver';
import { SiteResponseManagementResolver } from '../shared/resolvers/site-response-management.resolver';
import { WorkflowResolver } from '../shared/resolvers/workflow.resolver';
import { DesignHomepageComponent } from '../views/site/design-homepage/design-homepage.component';
import { DesignHomepageHtmlComponent } from '../views/site/design-homepage-html/design-homepage-html.component';
import { DesignComponent } from '../views/site/design/design.component';
import { DesignFormHtmlComponent } from '../views/site/design-form-html/design-form-html.component';
import { DocumentPublishingComponent } from '../views/site/document-publishing/document-publishing.component';
import { PermissionsComponent } from '../views/site/permissions/permissions.component';
import { PrintableFormComponent } from '../views/site/form/printable-form.component';
import { FormsComponent } from '../views/site/forms/forms.component';
import { FormsHtmlOnlyComponent } from '../views/site/forms-html-only/forms-html-only.component';
import { HomePageComponent } from '../views/site/home-page/home-page.component';
import { ManageComponent } from '../views/site/manage/manage.component';
import { PermissionsPreviewComponent } from '../views/site/permissions-preview/permissions-preview.component';
import { ResponseManagementComponent } from '../views/site/response-management/response-management.component';
import { StructureComponent } from '../views/site/structure/structure.component';
import { SummaryComponent } from '../views/site/summary/summary.component';
import { WorkflowComponent } from '../views/site/workflow/workflow.component';
import { WorkflowsComponent } from '../views/site/workflows/workflows.component';

const routes: Routes = [
    // Site/data collection-related routes:
    {
        path: '',
        pathMatch: "full", 
        canActivate: [HomePageRouteGuard],
        children: [] //VNEXT-1430: KLW - Due to changes in Angular 14, all routes must have either a component, loadComponent, redirectTo, children or loadChildren
    }, 
    {
        path: 'home-page',
        component: HomePageComponent,
        data: {
            title: environment.sitename + ' - Home Page'
        }
    },
    {
        path: 'summary',
        component: SummaryComponent,
        data: {
            breadcrumbLabel: 'Site - Summary',
            requiredClaim: 'CanViewSiteSummary',
            redirectToWhenCannotActivate: 'structure',
            title: environment.sitename + ' - Summary'
        },
        canActivate: [ClaimGuard]
    },
    {
        path: 'design-homepage',
        component: DesignHomepageComponent,
        resolve: { homePage: SiteHomePageResolver },
        data: {
            breadcrumbLabel: 'Site - Design Home Page',
            requiredClaim: 'CanViewSiteSummary',
            redirectToWhenCannotActivate: 'summary',
            title: environment.sitename + ' - Edit Home Page'
        },
        canActivate: [ClaimGuard],
        canDeactivate: [UnsavedChangesGuard]
    },
    {
        path: 'design-homepage-html',
        component: DesignHomepageComponent,
        resolve: { homePage: SiteHomePageResolver },
        data: {
            breadcrumbLabel: 'Site - Design Home Page',
            requiredClaim: 'CanViewSiteSummary',
            redirectToWhenCannotActivate: 'summary',
            title: environment.sitename + ' - Edit Home Page'
        },
        canActivate: [ClaimGuard],
        canDeactivate: [UnsavedChangesGuard]
    },
    {
        path: 'manage',
        component: ManageComponent,
        resolve: { siteResponseManagementData: SiteResponseManagementResolver },
        data: {
            breadcrumbLabel: 'Manage',
            requiredClaim: 'CanViewSiteSummary',
            redirectToWhenCannotActivate: 'structure',
            title: environment.sitename + ' - Edit Home Page'
        },
        canActivate: [ClaimGuard]
    },
    {
        path: 'structure',
        children: [{
            // No ClaimGuard here since it's dynamic. Expectation is that api will only return what user is allowed to access
            // paths here look like...
            // structure/folder/<id>
            // structure/folder/<id>/form/<id>
            path: '**',
            component: StructureComponent,
            resolve: { folder: FolderResolver, formInstance: FormInstanceResolver },
            // this allows current route to be refreshed when updates are made in the tree
            // https://medium.com/angular-in-depth/refresh-current-route-in-angular-512a19d58f6e
            runGuardsAndResolvers: 'always',
            // https://stackoverflow.com/questions/35922071/warn-user-of-unsaved-changes-before-leaving-page
            canDeactivate: [UnsavedChangesGuard] // Note:  this is part of the mechanism that can cause a browser to display a dialogue when the user is navigating away and there are unsaved changes.
        }],
        data: {
            title: environment.sitename + ' - Structure'
        },
    },

    // Form-related routes:
    {
        path: 'form-catalog',
        component: FormsComponent,
        data: {
            breadcrumbLabel: 'Form Template Catalog',
            requiredClaim: 'CanViewSiteFormCatalog',
            redirectToWhenCannotActivate: 'structure',
            title: environment.sitename + ' - Form Templates'
        },
        canActivate: [ClaimGuard],
        // https://medium.com/angular-in-depth/refresh-current-route-in-angular-512a19d58f6e
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'form-catalog-html-edit-only',
        component: FormsHtmlOnlyComponent,
        data: {
            breadcrumbLabel: 'Form Template HTML Catalog',
            requiredClaim: 'CanEditFormTemplateHtmlOnly',
            redirectToWhenCannotActivate: 'structure',
            title: environment.sitename + ' - Form Template HTML'
        },
        canActivate: [ClaimGuard],
        // https://medium.com/angular-in-depth/refresh-current-route-in-angular-512a19d58f6e
        runGuardsAndResolvers: 'always'
    },
    {
        path: 'form-field-constraints',
        component: FormFieldConstraintsComponent,
        data: {
            breadcrumbLabel: 'Form Field Constraints',
            requiredClaim: 'CanBuildConstraints',
            redirectToWhenCannotActivate: 'form-catalog',
            title: environment.sitename + ' - Form Field Constraints'
        },
        canActivate: [ClaimGuard],
        // https://medium.com/angular-in-depth/refresh-current-route-in-angular-512a19d58f6e
        runGuardsAndResolvers: 'always'
    },

    // For VNEXT-761
    {
        // No ClaimGuard here since api will only return FormInstances the user has access to
        path: 'printable-form/:id',
        component: PrintableFormComponent,
        resolve: { formInstanceHtml: PrintableFormInstanceResolver },
        data: { breadcrumbLabel: 'Form' }
    },
    {
        // No ClaimGuard here since api will only return a Form if the user has access to it
        path: 'design/:id',
        component: DesignComponent,
        resolve: { form: FormResolver },
        data: {
            breadcrumbLabel: 'Form Design',
            title: environment.sitename + ' - Design'
        },
        canDeactivate: [UnsavedChangesGuard]
    },
    {
        // No ClaimGuard here since api will only return a Form if the user has access to it
        path: 'design-html-edit-only/:id',
        component: DesignFormHtmlComponent,
        resolve: { form: FormResolver },
        data: {
            breadcrumbLabel: 'Form HTML Design',
            title: environment.sitename + ' - HTML Design'
        },
        canDeactivate: [UnsavedChangesGuard]
    },

    // Workflow-related routes:
    {
        path: 'workflow-catalog',
        component: WorkflowsComponent,
        data: {
            breadcrumbLabel: 'Workflow Catalog',
            requiredClaim: 'CanViewSiteWorkflowCatalog',
            redirectToWhenCannotActivate: 'structure',
            title: environment.sitename + ' - Workflows'
        },
        canActivate: [ClaimGuard]
    },
    {
        // No ClaimGuard here since api will only return a Workflow if the user has access to it
        path: 'design-workflow/:id',
        component: WorkflowComponent,
        resolve: { workflow: WorkflowResolver },
        data: {
            breadcrumbLabel: 'Workflow',
            title: environment.sitename + ' Workflow'
        },
        canDeactivate: [UnsavedChangesGuard]
    },

    {
        path: 'response-management',
        component: ResponseManagementComponent,
        data: {
            breadcrumbLabel: 'Response Management',
            title: environment.sitename + ' - Site Response Management'
        }
    },

    {
        path: 'document-publishing',
        component: DocumentPublishingComponent,
        resolve: { documentPublishing: DocumentPublishingSettingsResolver },
        data: {
            breadcrumbLabel: 'Document Publishing',
            requiredClaim: 'CanViewSiteWorkflowCatalog',
            redirectToWhenCannotActivate: 'structure',
            title: environment.sitename + ' - Document Publishing'
        },
        canActivate: [ClaimGuard]
    },

    {
        path: 'notification-center',
        component: NotificationCenterComponent,
        data: {
            breadcrumbLabel: 'Notification Center',
            title: environment.sitename + ' - Notification Center'
        },
        resolve: { notifications: NotificationCenterResolver }
    },

    // Permission-related routes:
    {
        path: 'permissions-preview',
        component: PermissionsPreviewComponent,
        data: {
            breadcrumbLabel: 'Permissions',
            title: environment.sitename + ' - Permissions Preview'
        }
    },
    {
        path: 'permissions',
        component: PermissionsComponent,
        data: {
            breadcrumbLabel: 'Permissions',
            requiredClaim: 'CanViewSitePermissions',
            redirectToWhenCannotActivate: 'structure',
            title: environment.sitename + ' - Permissions'
        },
        canActivate: [ClaimGuard]
    },

    {
        path: 'create-folder-template/:id',
        component: MagicStructureSetupDialog,
        resolve: { folder: FolderResolver },
        data: { breadcrumbLabel: 'Create Folder Template' }
    },
    {
        path: 'create-form-template/:id',
        component: MagicStructureSetupDialog,
        resolve: { formInstance: FormInstanceResolver },
        data: { breadcrumbLabel: 'Create Form Template' }
    }
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule]
})
export class SiteRoutingModule { }
